import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ProtectedRoute } from './components/ProtectedRoute';
import { SiteShell } from './components/layout';
import BackToTop from './components/BackToTop';
import CookieBanner from './components/CookieBanner';
import { pageTransition } from './utils/animations';
import { HelmetProvider } from 'react-helmet-async';
import TermsAndConditions from './pages/TermsAndConditions';
import { ColorModeProvider } from './contexts/ColorModeContext';
import logger from './utils/logger';
import NewsletterConfirmation from './pages/NewsletterConfirmation';
import NewsletterUnsubscribe from './pages/NewsletterUnsubscribe';
import NewsletterRemove from './pages/NewsletterRemove';
import NewsletterSubscribe from './pages/NewsletterSubscribe';
import NotFound from './pages/NotFound';

const Home = lazy(() => import('./pages/Home'));
const Services = lazy(() => import('./pages/Services'));
const ServiceDetail = lazy(() => import('./pages/ServiceDetail'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const Admin = lazy(() => import('./pages/Admin'));
const Login = lazy(() => import('./pages/Login'));
const PostEditor = lazy(() => import('./components/PostEditor'));
const TestimonialEditor = lazy(() => import('./components/TestimonialEditor'));
const NewsletterEditor = lazy(() => import('./components/NewsletterEditor'));
const NewsletterAnalytics = lazy(() => import('./pages/NewsletterAnalytics'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const AboutUs = lazy(() => import('./pages/AboutUs'));
const Contact = lazy(() => import('./pages/Contact'));

const Loading = () => (
  <motion.div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '60vh',
      background: 'var(--bg)',
      color: 'var(--ink-dim)',
      fontFamily: 'var(--font-mono)',
      fontSize: 13,
    }}
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
  >
    loading…
  </motion.div>
);

interface AppErrorBoundaryState {
  hasError: boolean;
  errorDetails: string;
}

class AppErrorBoundary extends React.Component<
  { children: React.ReactNode },
  AppErrorBoundaryState
> {
  state: AppErrorBoundaryState = { hasError: false, errorDetails: '' };

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { hasError: true, errorDetails: error.message || '' };
  }

  componentDidMount() {
    if (!this.state.hasError) {
      sessionStorage.removeItem('error_reload_count');
    }
  }

  componentDidCatch(error: Error): void {
    logger.error('Error caught by boundary', {
      component: 'ErrorBoundary',
      action: 'componentDidCatch',
      error: error.message,
      stack: error.stack,
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            background: 'var(--bg)',
            color: 'var(--ink)',
            fontFamily: 'var(--font-body)',
          }}
        >
          <div style={{ textAlign: 'center', maxWidth: 480 }}>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 500 }}>
              Something went wrong
            </h1>
            <p style={{ color: 'var(--ink-dim)', marginTop: 12 }}>
              {this.state.errorDetails ||
                "We're sorry, but there was an error loading this page."}
            </p>
            <button
              onClick={() => {
                const reloadCount = parseInt(
                  sessionStorage.getItem('error_reload_count') || '0',
                  10
                );
                if (reloadCount >= 3) {
                  sessionStorage.removeItem('error_reload_count');
                  window.location.href = '/';
                } else {
                  sessionStorage.setItem('error_reload_count', String(reloadCount + 1));
                  window.location.reload();
                }
              }}
              className="ds-btn ds-btn--primary"
              style={{ marginTop: 20 }}
              type="button"
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const AnimatedRoutes = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    logger.info('Route changed', {
      component: 'AnimatedRoutes',
      action: 'navigation',
      path: location.pathname,
      search: location.search,
    });
  }, [location]);

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial="hidden"
        animate="visible"
        exit="exit"
        variants={pageTransition}
      >
        <Routes location={location}>
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<Services />} />
          <Route path="/services/:serviceId" element={<ServiceDetail />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/login" element={<Login />} />
          <Route path="/about-us" element={<AboutUs />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute requireAdmin>
                <Admin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/posts/new"
            element={
              <ProtectedRoute requireAdmin>
                <PostEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/posts/:id"
            element={
              <ProtectedRoute requireAdmin>
                <PostEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/testimonials/new"
            element={
              <ProtectedRoute requireAdmin>
                <TestimonialEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/testimonials/:id"
            element={
              <ProtectedRoute requireAdmin>
                <TestimonialEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/newsletters/new"
            element={
              <ProtectedRoute requireAdmin>
                <NewsletterEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/newsletters/:id"
            element={
              <ProtectedRoute requireAdmin>
                <NewsletterEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/newsletters/:id/analytics"
            element={
              <ProtectedRoute requireAdmin>
                <NewsletterAnalytics />
              </ProtectedRoute>
            }
          />
          <Route path="/newsletter/confirm/:token" element={<NewsletterConfirmation />} />
          <Route path="/newsletter/unsubscribe/:token" element={<NewsletterUnsubscribe />} />
          <Route path="/newsletter/remove/:token" element={<NewsletterRemove />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
};

export const App = () => {
  useEffect(() => {
    logger.info('Application initialized', {
      component: 'App',
      action: 'init',
      environment: process.env.NODE_ENV,
    });
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      logger.error('Unhandled promise rejection', {
        component: 'App',
        action: 'unhandledRejection',
        error: event.reason?.message || 'Unknown error',
        stack: event.reason?.stack,
      });
    };
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return () => window.removeEventListener('unhandledrejection', handleUnhandledRejection);
  }, []);

  return (
    <ColorModeProvider>
      <HelmetProvider>
        <AppErrorBoundary>
          <Router>
            <SiteShell>
              <Suspense fallback={<Loading />}>
                <AnimatedRoutes />
              </Suspense>
            </SiteShell>
            <BackToTop />
            <CookieBanner />
          </Router>
        </AppErrorBoundary>
      </HelmetProvider>
    </ColorModeProvider>
  );
};

export default App;
